import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { findCommodity } from "@/data/commodities";
import { setPageMeta, injectJsonLd, SITE } from "@/lib/seo";
import { GoldButton } from "@/components/ds";
import { Spotlight } from "@/components/ds/Spotlight";
import { AssayPanel } from "@/components/ds/AssayPanel";
import { Reveal } from "@/components/site/Reveal";

const CommodityDetail = () => {
  const { slug } = useParams();
  const c = findCommodity(slug);

  useEffect(() => {
    if (!c) return;
    setPageMeta({
      title: `${c.name} | Zorem`,
      description: `${c.desc.slice(0, 150)}…`,
      path: `/commodities/${c.slug}`,
    });
    injectJsonLd("commodity-jsonld", {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          name: `${c.name} — ${c.spec}`,
          description: c.desc,
          inLanguage: "en",
          about: { "@type": "Thing", name: c.name, description: c.spec },
          provider: { "@type": "Organization", name: "Zorem Commodities LLC", url: SITE },
          image: `${SITE}${c.img}`,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Commodities", item: `${SITE}/#commodities` },
            { "@type": "ListItem", position: 3, name: c.name },
          ],
        },
      ],
    });
    return () => document.getElementById("commodity-jsonld")?.remove();
  }, [c]);

  if (!c) return <Navigate to="/" replace />;

  return (
    <div data-testid={`commodity-page-${c.slug}`} className="bg-[#0A0A0B] min-h-screen">
      <div className="pt-36 pb-12 bg-[#121215] hairline-b">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link to="/#commodities" data-testid="commodity-back" className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#C9A227] hover:text-[#E8C85A] transition-colors">
            ← Commodity Portfolio
          </Link>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 border border-zinc-700 px-3 py-1">
              Group {c.group}
            </span>
            {c.tag && (
              <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#C9A227] border border-[#C9A227]/40 px-3 py-1">
                {c.tag}
              </span>
            )}
          </div>
          <h1 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-wide text-white">
            {c.name}
          </h1>
          <p className="mt-4 font-mono2 text-[12px] uppercase tracking-[0.22em] text-[#D4AF37]">{c.spec}</p>
          <p className="mt-2 font-mono2 text-[11px] uppercase tracking-[0.18em] text-zinc-500">Origin · {c.origin}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-12 items-start">
        <Reveal className="lg:sticky lg:top-32 self-start">
          <div className="relative">
            <div className="absolute -inset-3 card-line pointer-events-none" />
            <Spotlight>
              <img src={c.img} alt={`${c.name} — ${c.spec}`} className="w-full aspect-[4/5] object-cover" loading="eager" />
            </Spotlight>
          </div>
        </Reveal>
        <div>
          <p className="text-base text-zinc-400 leading-relaxed">{c.desc}</p>
          <p className="mt-4 text-base text-zinc-400 leading-relaxed">
            How {c.name.toLowerCase().includes("gold") ? "this metal" : "this commodity"} clears the
            Zorem network: origin is verified against corridor documentation before any term sheet
            is drafted; independent assay precedes export; custody is serialized through bonded
            logistics to a recognized vault or refinery; and settlement executes against presented
            documents through the agreed instrument — SBLC, DLC, or confirmed wire. Indicative
            figures on this page reflect corridor norms; the numbers that settle are the ones on the
            destination assay certificate and the written contract.
          </p>
          <p className="mt-4 text-base text-zinc-400 leading-relaxed">
            Every engagement begins with a mandate and a trial lift — deliberately small, fully
            documented, and audited end-to-end — before any program scales. That sequence is how
            both sides prove the corridor without risking serious capital.
          </p>
          {c.assay ? (
            <div className="mt-8">
              <AssayPanel assay={c.assay} testid={`assay-${c.slug}`} />
            </div>
          ) : (
            <div className="mt-8 card-line bg-[#121215] px-6 py-5 font-mono2 text-[11px] uppercase tracking-[0.2em] text-zinc-400">
              Purity & grading confirmed per mandate through independent assay
            </div>
          )}
          <div className="mt-8 space-y-2.5">
            {c.standards.map((s) => (
              <div key={s} className="flex items-center gap-3 text-sm text-zinc-400">
                <span className="w-1.5 h-1.5 rotate-45 bg-[#C9A227] shrink-0" aria-hidden="true" /> {s}
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <GoldButton to="/mandate" testid={`commodity-cta-${c.slug}`}>Submit a Mandate</GoldButton>
          </div>
        </div>
      </div>

      {c.gallery.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
          <h2 className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#C9A227] mb-6">From the corridor</h2>
          <div className="grid sm:grid-cols-3 gap-px bg-transparent" data-testid={`gallery-${c.slug}`}>
            {c.gallery.map((g, i) => (
              <Spotlight key={i} className="card-line">
                <img src={g} alt={`${c.name} corridor photograph ${i + 1}`} loading="lazy"
                  className="w-full aspect-[4/3] object-cover hover:scale-[1.04] transition-transform duration-700" />
              </Spotlight>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommodityDetail;
